import type { CancellationResponse, CancellationPayload, SubscriptionResponse, CancellationEligibility } from '@cvo/domain';
import { getIsoDate } from '@cvo/domain/utils/dateUtils';
import { useSubscriptionStore } from './subscriptionStore';

interface OffboardingState {
  reason: CancellationPayload['reason'] | null;
  reasonText: CancellationPayload['reason_text'];
  cancelType: CancellationEligibility;
}

export const useOffboardingStore = defineStore('offboarding', () => {
  const subscriptionStore = useSubscriptionStore();
  const api = useApi();

  // ===== STATE ===== //

  const cancellationSelection = reactive<OffboardingState>({
    reason: null,
    reasonText: '',
    cancelType: 'END_OF_COMMITMENT',
  });

  const cancellationResponse = ref<CancellationResponse | null>(null);
  const cancelFlowOpened = ref(false);
  // Kept after the flow closes so the success and error modals stay mounted.
  const subscriptionToCancel = ref<SubscriptionResponse | null>(null);

  const cancellationReason = computed(() => {
    if (cancellationSelection.reason) {
      return {
        code: cancellationSelection.reason,
        text: cancellationSelection.reasonText || '',
      };
    }
    return undefined;
  });

  const cancellationRequest = useAsyncRequest(performCancellation);

  // ===== ACTIONS ===== //

  function startOffboardingFlow(subscription: SubscriptionResponse) {
    resetAll();
    subscriptionToCancel.value = subscription;
    cancelFlowOpened.value = true;
  }

  function resetRequestStatus() {
    cancelFlowOpened.value = false;
    cancellationRequest.clear();
  }

  function resetCancelFlow() {
    cancellationSelection.cancelType = 'END_OF_COMMITMENT';
    cancellationSelection.reason = null;
    cancellationSelection.reasonText = '';
  }

  function resetAll() {
    resetCancelFlow();
    resetRequestStatus();
  }

  async function performCancellation(subscription: SubscriptionResponse) {
    try {
      cancellationResponse.value = await api.subscription.cancelSubscription(
        subscription.id,
        cancellationSelection.cancelType,
        cancellationReason.value,
      ).then(res => res.data);

      let optimisticSubscriptionUpdate: RecursivePartial<SubscriptionResponse> | undefined;
      if (cancellationSelection.cancelType === 'IMMEDIATE' || cancellationSelection.cancelType === 'IMMEDIATE_NO_REFUND') {
        // If the user chose immediate cancellation, we optimistically update the subscription status to CANCELLED
        // to avoid seeing "Keep my plan" button when it shouldn't.
        optimisticSubscriptionUpdate = getCancelledSubscriptionStatus();
      }

      await subscriptionStore.refreshSubscription(subscription.id, optimisticSubscriptionUpdate);
    } finally {
      cancelFlowOpened.value = false;
    }
  }

  return {
    ...toRefs(cancellationSelection),
    cancellationResponse,
    subscriptionToCancel,

    // Async request states
    isLoading: cancellationRequest.isLoading,
    cancelError: cancellationRequest.error,
    cancelSucceeded: cancellationRequest.success,

    cancelFlowOpened,

    startOffboardingFlow,
    resetCancelFlow,
    reset: resetAll,
    cancelSubscription: cancellationRequest.run,
  };
});

/** Give the Subscription attributes that make a subscription cancelled
 * @returns a Partial object of SubscriptionResponse with CANCELLED status
 */
function getCancelledSubscriptionStatus(): RecursivePartial<SubscriptionResponse> {
  return {
    status: 'CANCELLED',
    displayStatus: 'CANCELLED',
    cancellation: {
      status: 'CANCELLED',
      effectiveDate: getIsoDate(new Date()),
    },
  };
}